import type { MyChargerConnection, MyChargerConnectionState } from './api';

/**
 * Register-first (2026-10-02): what My Home, Add charger and the charger
 * page show about connecting a charger that may never have been online.
 *
 * The API sends MyCharger.connection (URL, identity, password mode, setup
 * state); this file turns it into what the screens decide on, so the rules
 * live in one tested place:
 *   - a never-connected charger shows the setup card and a "Waiting for
 *     first connection" pill, never "Offline" and never the offline
 *     recovery flow (there is nothing to recover yet)
 *   - a connected charger keeps its normal status pill, and the setup card
 *     folds to a "Connection details" row
 *   - an API older than register-first sends no block; every charger it
 *     knows registered online, so it reads as connected, with the URL
 *     built from the display fallback base
 */

export interface ConnectionChargerLike {
  identity: string;
  connection?: MyChargerConnection | null;
}

export interface ConnectionView {
  url: string;
  identity: string;
  passwordMode: MyChargerConnection['password_mode'];
  state: MyChargerConnectionState;
  /** True until the charger has connected (or finished provisioning). */
  settingUp: boolean;
  firstConnection: MyChargerConnection['first_connection'];
}

/** base + '/' + identity with exactly one slash. */
export function joinConnectionUrl(base: string, identity: string): string {
  return `${base.replace(/\/+$/, '')}/${identity}`;
}

export function connectionViewFor(charger: ConnectionChargerLike, fallbackBase: string): ConnectionView {
  const c = charger.connection;
  if (!c) {
    return {
      url: joinConnectionUrl(fallbackBase, charger.identity),
      identity: charger.identity,
      passwordMode: 'password',
      state: 'connected',
      settingUp: false,
      firstConnection: null,
    };
  }
  return {
    url: c.url,
    identity: c.identity,
    passwordMode: c.password_mode,
    state: c.state,
    settingUp: c.state !== 'connected',
    firstConnection: c.first_connection,
  };
}

/** Whether the first-connection window is open NOW. The server's
 *  window_open is true as of the read; a screen left open (the register
 *  sheet, Add a charger) must not keep saying "Setup is open until ..."
 *  after window_ends_at passes, so the end time decides too. */
export function firstConnectionWindowOpen(
  fc: MyChargerConnection['first_connection'] | null | undefined,
  nowMs: number,
): boolean {
  if (!fc || !fc.window_open) return false;
  if (!fc.window_ends_at) return true;
  const ends = Date.parse(fc.window_ends_at);
  return Number.isFinite(ends) ? ends > nowMs : true;
}

/** Milliseconds until an open window closes, for a one-shot redraw timer;
 *  null when there is nothing to wait for. */
export function msUntilWindowCloses(
  fc: MyChargerConnection['first_connection'] | null | undefined,
  nowMs: number,
): number | null {
  if (!firstConnectionWindowOpen(fc, nowMs) || !fc?.window_ends_at) return null;
  const ends = Date.parse(fc.window_ends_at);
  return Number.isFinite(ends) ? Math.max(0, ends - nowMs) : null;
}

/** Why the owner is asked for the password after first-connection setup:
 *  'window_expired' when a setup window closed with no key ever sent (the
 *  charger may refuse a blank password) and setup can still be opened
 *  again; 'key_failed' for every other needs_credentials (a refused or
 *  unfinished key, or every window used up). Null when the state is not
 *  needs_credentials. */
export function needsCredentialsReason(view: ConnectionView): 'window_expired' | 'key_failed' | null {
  if (view.state !== 'needs_credentials') return null;
  return view.firstConnection?.can_rearm ? 'window_expired' : 'key_failed';
}

/** Whether the card offers "Open setup for 30 minutes" beside the
 *  password: needs_credentials after an expired window, while a re-arm is
 *  left. (In first_connection mode the setup row offers it instead.) */
export function offersRearmBesidePassword(view: ConnectionView): boolean {
  return view.passwordMode === 'password' && needsCredentialsReason(view) === 'window_expired';
}

/** Whether the charger has never connected: the waiting pill, the setup
 *  card and the never-connected Start note key on this. Provisioning and
 *  needs_credentials count too: the charger is not usable yet. */
export function isAwaitingFirstConnection(charger: ConnectionChargerLike): boolean {
  const state = charger.connection?.state;
  return state !== undefined && state !== 'connected';
}

/** Models the register form warns about (they cannot connect to the EV
 *  Initiative network). The server also flags them; this keeps the warning
 *  working against a list that does not. */
const NETWORK_INCOMPATIBLE = ['ac elite home'];
export function isNetworkIncompatibleModel(model: string | null | undefined): boolean {
  return !!model && NETWORK_INCOMPATIBLE.includes(model.trim().toLowerCase());
}

/** The support form with the identity as its context, the link the 409
 *  carries; used when its body cannot be read. */
export function supportUrlForIdentity(identity: string): string {
  return `/support?charger=${encodeURIComponent(identity)}`;
}

/** The 409 the register endpoint answers for a taken identity, read from
 *  its body: the support link, or null for any other error. */
export function alreadyRegisteredFrom(body: unknown): { supportUrl: string } | null {
  const error = (body as { error?: { code?: unknown; support_url?: unknown } } | null)?.error;
  if (!error || error.code !== 'CHARGER_ALREADY_REGISTERED') return null;
  const url = typeof error.support_url === 'string' && error.support_url.startsWith('/support')
    ? error.support_url
    : '/support';
  return { supportUrl: url };
}

/** A revealed password is shown for display_seconds, then cleared. The
 *  timer functions are injectable so the clear is testable. Returns the
 *  cancel function (call it on unmount or when hiding early). */
export function scheduleRevealClear(
  displaySeconds: number,
  clear: () => void,
  timers: { setTimeout: (fn: () => void, ms: number) => unknown; clearTimeout: (id: unknown) => void } = {
    setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
    clearTimeout: (id) => globalThis.clearTimeout(id as ReturnType<typeof globalThis.setTimeout>),
  },
): () => void {
  const seconds = Number.isFinite(displaySeconds) && displaySeconds > 0 ? Math.min(displaySeconds, 300) : 60;
  const id = timers.setTimeout(clear, seconds * 1000);
  return () => timers.clearTimeout(id);
}

/** Seconds left on a reveal, for the countdown. Never negative. */
export function revealSecondsLeft(revealedAtMs: number, displaySeconds: number, nowMs: number): number {
  return Math.max(0, Math.ceil((revealedAtMs + displaySeconds * 1000 - nowMs) / 1000));
}
